/*
 * Personal Digital Spaces LLC ("COMPANY") CONFIDENTIAL
 * Unpublished Copyright (c) 2026 Personal Digital Spaces LLC, All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of COMPANY. The intellectual and technical concepts contained
 * herein are proprietary to COMPANY and may be covered by U.S. and Foreign Patents, patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from COMPANY.  Access to the source code contained herein is hereby forbidden to anyone except current COMPANY employees, managers, contractors or partners who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of  COMPANY.   ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */
import React, { useState } from 'react'
import {
  FormControl,
  FormLabel
} from '@chakra-ui/react'
import { useForm } from 'react-hook-form'
import { useAuth0 } from '@auth0/auth0-react'
import { startAssetsBatchProcessing, updateAssetsBatch } from '@/services/asset-batches'
import { useNavigate } from 'react-router-dom'
import { assetsBatchesUrls } from '@/configs/urls'
import ProcessBatch from '@/components/batches/ProcessBatch'
import ModalBodyProcessBatch from './components/ModalBodyProcessBatch'
import { columnsTable } from './handlers'
import FormWrapper from '@/components/forms/FormWrapper'
import { GENERAL_ERROR } from '@/configs/strings/message'
import CircleLoader from '@/components/loaders/CircleLoader'
import { formLeosToZetons } from '@/utils/pds'

const BatchForm = ({
  assetBatch,
  totalQueueData,
  className = ''
}) => {
  const { processingCostDetails } = assetBatch
  const [isLoading, setIsLoading] = useState(false)
  const { getAccessTokenSilently: getAccessToken } = useAuth0()
  const [hasAlert, setHasAlert] = useState(false)
  const navigate = useNavigate()
  const defaultValues = assetBatch
    ? { name: assetBatch.name }
    : {}
  const {
    handleSubmit,
    formState: { errors }
  } = useForm({ defaultValues })
  const onSubmit = async (data) => {
    setIsLoading(true)
    setHasAlert(false)
    try {
      await updateAssetsBatch(
        { id: assetBatch.id, formData: data },
        getAccessToken
      )
      await startAssetsBatchProcessing(assetBatch.id, getAccessToken)
      navigate(`/${assetsBatchesUrls.base}`, { replace: true })
    } catch (error) {
      setHasAlert(true)
    }

    setIsLoading(false)
  }
  const sendData = async () => await handleSubmit(onSubmit)()
  const totalCost = formLeosToZetons(processingCostDetails?.totalCost, false)
  const costPerAsset = formLeosToZetons(processingCostDetails?.averageCostPerAsset, false)
  const totalAssets = processingCostDetails?.totalAssets
  const rowTable = {
    totalCost,
    costPerAsset,
    numberAssets: totalAssets
  }

  if (isLoading) return <CircleLoader />

  return (
    <FormWrapper className={className} hasAlert={hasAlert}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="flex gap-3 mb-4">
          <div className="grow">
            <FormControl isInvalid={errors.name} className="mb-4">
              <FormLabel className="!font-bold">Batch Name</FormLabel>
              <div>
                { assetBatch.name }
              </div>
            </FormControl>
          </div>
        </div>
        <ProcessBatch
          alertMessage={GENERAL_ERROR}
          alertStatus="error"
          bodyModal={
            () => <ModalBodyProcessBatch
              costPerAsset={costPerAsset}
              totalAssets={totalAssets}
              totalCost={totalCost}
            />
          }
          columnsTable={columnsTable}
          hasAlert={hasAlert}
          labelContinue="Accept Terms + Register Batch"
          labelModalOnProcess="Register Assets"
          onProcess={sendData}
          rowTable={rowTable}
          setHasAlert={setHasAlert}
          titleModal="Register Multiple Assets"
          isInvalid={!totalQueueData}
        />
      </form>
    </FormWrapper>
  )
}

export default BatchForm
